import React, { useState } from 'react';
import { useClients } from '../hooks';
import { ClientCard, ClientForm } from '../components/clients';
import { PermissionsManager } from '../components/permissions';
import { Button, Modal, Spinner, Alert } from '../components/ui';
import { Plus } from 'lucide-react';

export const Clients: React.FC = () => {
  const { clients, loading, error, createClient, updateClient, deleteClient } = useClients();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingClient, setEditingClient] = useState<{ id: string; name: string; description?: string } | null>(null);
  const [managingPermissionsClient, setManagingPermissionsClient] = useState<{ id: string; name: string } | null>(null);

  const handleCreate = async (data: {
    name?: string;
    description?: string;
    color?: string;
    icon?: string;
    notes?: string;
    default_permission?: 'allow_all' | 'deny_all';
  }) => {
    if (!data.name) return;
    await createClient(data.name, data.description);
    setShowCreateModal(false);
  };

  const handleUpdate = async (data: {
    name?: string;
    description?: string;
    color?: string;
    icon?: string;
    notes?: string;
    default_permission?: 'allow_all' | 'deny_all';
  }) => {
    if (!editingClient) return;
    await updateClient(editingClient.id, data);
    setEditingClient(null);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this client? This will also disconnect all associated servers.')) {
      return;
    }
    try {
      await deleteClient(id);
    } catch (err) {
      console.error('Failed to delete client:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Clients</h1>
          <p className="text-gray-600 mt-1">
            Manage your MCP clients and their configurations
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => setShowCreateModal(true)}
        >
          <Plus size={18} className="mr-2" />
          Create Client
        </Button>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger">
          {error}
        </Alert>
      )}

      {/* Clients Grid */}
      {clients.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-600 mb-4">No clients yet</p>
          <Button
            variant="primary"
            onClick={() => setShowCreateModal(true)}
          >
            <Plus size={18} className="mr-2" />
            Create Your First Client
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {clients.map((client) => (
            <ClientCard
              key={client.id}
              {...client}
              onEdit={(id) => {
                const client = clients.find(c => c.id === id);
                if (client) {
                  setEditingClient(client);
                }
              }}
              onDelete={handleDelete}
              onManagePermissions={(id) => {
                const client = clients.find(c => c.id === id);
                if (client) {
                  setManagingPermissionsClient({ id: client.id, name: client.name });
                }
              }}
            />
          ))}
        </div>
      )}

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Client"
        size="md"
      >
        <ClientForm
          onSubmit={handleCreate}
          onCancel={() => setShowCreateModal(false)}
          submitLabel="Create Client"
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={!!editingClient}
        onClose={() => setEditingClient(null)}
        title="Edit Client"
        size="md"
      >
        {editingClient && (
          <ClientForm
            initialData={editingClient}
            onSubmit={handleUpdate}
            onCancel={() => setEditingClient(null)}
            submitLabel="Update Client"
          />
        )}
      </Modal>

      {/* Permissions Modal */}
      <Modal
        isOpen={!!managingPermissionsClient}
        onClose={() => setManagingPermissionsClient(null)}
        title=""
        size="xl"
      >
        {managingPermissionsClient && (
          <PermissionsManager
            clientId={managingPermissionsClient.id}
            clientName={managingPermissionsClient.name}
            onClose={() => setManagingPermissionsClient(null)}
          />
        )}
      </Modal>
    </div>
  );
};
